import {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
} from "react";
import { ENDPOINTS } from "../api/endpoints.js";
import apiClient, {
  authenticatedRequest,
  publicRequest,
} from "../api/clients.js";

// Create Auth Context
const JobAuthContext = createContext(null);

// Auth Provider Component
export const JobAuthProvider = ({ children }) => {
  // const [userId, setUserId] = useState("");
  // const [userEmail, setUserEmail] = useState("");

  const [accessToken, setAccessToken] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem("jobToken");
        if (storedToken) {
          setAccessToken(storedToken);
          apiClient.setAuthToken(storedToken);
        }
      } catch (err) {
        console.error("Error initializing auth:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const saveAuthData = (userData) => {
    localStorage.setItem("jobUserId", userData.user_id);
    localStorage.setItem("jobToken", userData.access);
    localStorage.setItem("jobRefreshToken", userData.refresh);
  };

  // Set auth token helper
  const setAuthToken = useCallback((newToken) => {
    if (newToken) {
      setAccessToken(newToken);
      localStorage.setItem("jobToken", newToken);
    } else {
      setAccessToken(null);
      localStorage.removeItem("jobToken");
    }
  }, []);

  // Login function - Updated to handle FormData
  const login = useCallback(
    async (credentials) => {
      let email, password;

      if (credentials instanceof FormData) {
        email = credentials.get("email");
        password = credentials.get("password");
      } else {
        email = credentials.email;
        password = credentials.password;
      }

      if (!email || !password) {
        const error = new Error("Email and password are required");
        setError(error.message);
        throw error;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await publicRequest.post(
          ENDPOINTS.AUTH.LOGIN,
          credentials,
          {
            skipAuthRedirect: true, // Prevent automatic redirect on 401
          }
        );

        // Check if response indicates success
        if (response.data.status === true && response.data.data) {
          const token = response.data.data.access;

          if (token) {
            setAuthToken(token);
          }

          const userData = response.data.data;
          saveAuthData(userData);
        }

        return response.data;
      } catch (err) {
        // console.log("🚨 Login Error Details:", err);

        let errorMessage = "Login failed. Please try again.";

        if (err.response) {
          // Server responded with error - use your API's response structure
          const errorData = err.response.data;

          if (errorData.message) {
            // Use the message from your API response
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.code === 401) {
            errorMessage =
              "Invalid credentials. Please check your email and password.";
          }
        } else if (err.request) {
          // Network error
          errorMessage =
            "Network error: Unable to connect to server. Please check your internet connection.";
        } else {
          // Other error
          errorMessage = err.message || "An unexpected error occurred";
        }

        setError(errorMessage);

        const loginError = new Error(errorMessage);
        loginError.response = err.response;
        loginError.status = err.response?.status;
        loginError.code = err.response?.data?.code;
        loginError.apiStatus = err.response?.data?.status; // Your API's status field

        throw loginError;
      } finally {
        setLoading(false);
      }
    },
    [setAuthToken, setLoading, setError]
  );

  // Register function - Updated to handle FormData if needed
  const register = useCallback(async (formData) => {
    // Handle both FormData and regular object
    try {
      setLoading(true);
      setError(null);
      const response = await publicRequest.post(
        ENDPOINTS.AUTH.REGISTER,
        formData
      );
      console.log("✅ API Response:", response);
      // Some APIs auto-login after registration
      // if (response.data.token || response.data.accessToken) {
      //   const authToken = response.data.token || response.data.accessToken;
      //   setAuthToken(authToken);

      //   if (response.data.user) {
      //     setUser(response.data.user);
      //   }
      // }
      return response.data;
    } catch (err) {
      let errorMessage = "Signup failed. Please try again.";

      if (err.response) {
        // Server responded with error - use your API's response structure
        const errorData = err.response.data;

        if (errorData.message) {
          // Use the message from your API response
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.code === 401) {
          errorMessage =
            "Invalid credentials. Please check your email and password.";
        }
      } else if (err.request) {
        // Network error
        errorMessage =
          "Network error: Unable to connect to server. Please check your internet connection.";
      } else {
        // Other error
        errorMessage = err.message || "An unexpected error occurred";
      }

      setError(errorMessage);

      const signupError = new Error(errorMessage);
      signupError.response = err.response;
      signupError.status = err.response?.status;
      signupError.code = err.response?.data?.code;
      signupError.apiStatus = err.response?.data?.status; // Your API's status field

      throw signupError;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  // const logout = useCallback(
  //   async (userId) => {
  //     try {
  //       setLoading(true);
  //       await apiClient.post(ENDPOINTS.AUTH.LOGOUT(userId), refreshTokenState);
  //     } catch (err) {
  //       console.error("Logout API error:", err);
  //     } finally {
  //       // Always clear local state regardless of API response
  //       localStorage.removeItem("jobUserId");
  //       localStorage.removeItem("jobToken");
  //       localStorage.removeItem("jobRefreshToken");
  //       setLoading(false);
  //     }
  //   },
  //   [refreshTokenState]
  // );

  //Password reset Email
  const resetEmail = useCallback(async (email) => {
    try {
      setLoading(true);
      setError(null);
      const response = await publicRequest.post(
        ENDPOINTS.AUTH.FORGOT_PASSWORD,
        { email: email }
      );
      return response;
    } catch (err) {
      console.error("Password-reset:", err);
      // If refresh fails, clear auth state
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password function
  const changePassword = useCallback(async (userId, passwordData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedRequest.post(
        ENDPOINTS.AUTH.RESET_PASSWORD(userId),
        passwordData
      );
      return response;
    } catch (err) {
      setError(err.message || "Password reset failed");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    // user,
    // userEmail,
    // userId,
    loading,
    error,
    isAuthenticated: !!accessToken,
    login,
    register,
    resetEmail,
    changePassword,

    // logout,
  };

  return (
    <JobAuthContext.Provider value={value}>{children}</JobAuthContext.Provider>
  );
};

// Main useAuth hook
export const useAuth = () => {
  const context = useContext(JobAuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Individual hooks for specific operations (optional - for more granular usage)
export const useLogin = () => {
  const { login, loading, error } = useAuth();
  return { login, loading, error };
};

export const useRegister = () => {
  const { register, loading, error } = useAuth();
  return { register, loading, error };
};

export const useLogout = () => {
  const { logout } = useAuth();
  return logout;
};

export const useChangePassword = () => {
  const { changePassword, loading } = useAuth();
  return { changePassword, loading };
};

export const useResetEmail = () => {
  const { resetEmail, loading } = useAuth();
  return { resetEmail, loading };
};
